import { useEffect, useRef, useState } from 'react';
import {
  profileBlocked,
  profileNeedsAction,
  type MiceNeedsOrderRequest,
  type MiceNeedView,
} from '@voll-intelligence/domain';
import { currentLocale, t } from '@web/i18n';
import { Button, Checkbox, Dialog, DialogContent, DialogTitle } from '@web/ui/core';
import {
  summarizePurchase,
  type MiceOrderDirection,
  type MicePurchaseSkip,
} from '../travelers/purchase_summary';
import { formatMoney } from './need_format';

/** Um sentido tirado da compra de uma necessidade. */
type MiceSkippedLeg = MiceNeedsOrderRequest['skip'][number];

interface PurchaseReviewDialogProps {
  /** Necessidades aprovadas que podem virar pedido; `null` fecha o diálogo. */
  needs: MiceNeedView[] | null;
  /** `MICE_PRICE_DRIFT_TOLERANCE` do projeto: o teto de cada bilhete. */
  priceTolerance: number;
  onCancel: () => void;
  /** "Completar cadastro" de quem está com cadastro incompleto: abre o painel do viajante. */
  onCompleteProfile: (need: MiceNeedView) => void;
  onConfirm: (needIds: string[], skip: MiceSkippedLeg[]) => void;
}

/** Quem tem voucher: tirar um sentido (o voucher cobre esse trecho) ou comprar tudo. */
type VoucherChoice = MiceOrderDirection | 'buy';

const KNOWN_CARRIERS = new Set(['LA', 'G3', 'AD']);
const carrierName = (code: string): string =>
  KNOWN_CARRIERS.has(code) ? t(`mice.carrier.${code as 'LA' | 'G3' | 'AD'}`) : code;

const travelerOf = (need: MiceNeedView): string => need.travelerName?.trim() || need.travelerEmail;
const voucherAlert = (need: MiceNeedView): string | null =>
  need.recommendation?.alerts.find((a) => a.code === 'existing_voucher')?.message ?? null;
/** O cadastro trava a compra: a checagem, ou o aviso antigo sem ela (o mesmo do domínio). */
const isIncomplete = (need: MiceNeedView): boolean => profileBlocked(need.profileCheck, need.error);
/** Checagem pendente: sem "comprar assim" (o servidor também não compra); só o aviso antigo tem. */
const checkPending = (need: MiceNeedView): boolean => profileNeedsAction(need.profileCheck);

/** Quem pede uma decisão antes da compra: fica fora até o operador marcar "comprar assim". */
const pendingOf = (need: MiceNeedView): boolean =>
  voucherAlert(need) !== null || isIncomplete(need);

function PendingGroup(props: {
  title: string;
  tone: 'amber' | 'red';
  needs: MiceNeedView[];
  detail: (need: MiceNeedView) => string | null;
  control: (need: MiceNeedView) => React.ReactNode;
}): React.JSX.Element | null {
  if (props.needs.length === 0) return null;
  const head =
    props.tone === 'amber'
      ? 'bg-tone-amber-tint text-tone-amber-ink'
      : 'bg-tone-red-tint text-tone-red-ink';
  return (
    <div className="overflow-hidden rounded-md border border-border">
      <div className={`px-3 py-2 text-meta font-medium ${head}`}>{props.title}</div>
      <ul>
        {props.needs.map((need) => (
          <li
            key={need.id}
            className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-3 py-2 text-meta"
          >
            <span className="min-w-0">
              <span className="font-medium text-foreground">{travelerOf(need)}</span>
              {props.detail(need) ? (
                <span className="block text-micro text-muted-foreground">{props.detail(need)}</span>
              ) : null}
            </span>
            {props.control(need)}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Cadastro incompleto: "Completar cadastro" e, com a checagem pendente, só "Deixar fora" (spec B3);
 * o aviso antigo sem checagem mantém o "Comprar assim".
 */
function IncompleteControls(
  props: Readonly<{
    need: MiceNeedView;
    included: boolean;
    onToggle: (on: boolean) => void;
    onCompleteProfile: (need: MiceNeedView) => void;
  }>,
): React.JSX.Element {
  const name = travelerOf(props.need);
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Button
        size="sm"
        variant="outline"
        aria-label={t('mice.travelers.actionFor', {
          action: t('mice.travelers.next.complete_profile'),
          name,
        })}
        onClick={() => {
          props.onCompleteProfile(props.need);
        }}
      >
        {t('mice.travelers.next.complete_profile')}
      </Button>
      {checkPending(props.need) ? (
        <Button
          size="sm"
          variant="primary"
          aria-pressed
          disabled
          aria-label={t('mice.travelers.actionFor', { action: t('mice.purchase.leaveOut'), name })}
        >
          {t('mice.purchase.leaveOut')}
        </Button>
      ) : (
        <Checkbox
          label={t('mice.purchase.buyIncomplete', { name })}
          checked={props.included}
          onChange={props.onToggle}
        />
      )}
    </span>
  );
}

const CHOICE_LABEL = {
  OUTWARD: { short: 'mice.purchase.skipOutward', full: 'mice.purchase.skipOutwardOf' },
  RETURN: { short: 'mice.purchase.skipReturn', full: 'mice.purchase.skipReturnOf' },
  buy: { short: 'mice.purchase.buyAnywayShort', full: 'mice.purchase.buyAnyway' },
} as const;

/** O alerta não diz qual trecho o voucher cobre: o operador escolhe. Nada marcado = fora. */
function VoucherChoices(
  props: Readonly<{
    need: MiceNeedView;
    value: VoucherChoice | undefined;
    onChange: (value: VoucherChoice | undefined) => void;
  }>,
): React.JSX.Element {
  const rec = props.need.recommendation;
  const options: VoucherChoice[] =
    rec?.outbound && rec.inbound ? ['OUTWARD', 'RETURN', 'buy'] : ['buy'];
  return (
    <span className="flex flex-wrap gap-1">
      {options.map((option) => (
        <Button
          key={option}
          size="sm"
          variant={props.value === option ? 'primary' : 'outline'}
          aria-pressed={props.value === option}
          aria-label={t(CHOICE_LABEL[option].full, { name: travelerOf(props.need) })}
          onClick={() => {
            props.onChange(props.value === option ? undefined : option);
          }}
        >
          {t(CHOICE_LABEL[option].short)}
        </Button>
      ))}
    </span>
  );
}

function Totals({ needs }: Readonly<{ needs: MiceNeedView[] }>): React.JSX.Element {
  const s = summarizePurchase(needs);
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-md border border-border p-3">
          <div className="text-micro text-muted-foreground">{t('mice.purchase.tickets')}</div>
          <div className="font-serif text-xl font-semibold">{s.tickets}</div>
        </div>
        <div className="rounded-md border border-border p-3">
          <div className="text-micro text-muted-foreground">{t('mice.purchase.total')}</div>
          <div className="font-serif text-xl font-semibold">
            {s.total ? formatMoney(s.total) : '—'}
          </div>
        </div>
      </div>
      {s.byCarrier.length > 0 ? (
        <div className="flex flex-col gap-1">
          <span className="text-micro text-muted-foreground">{t('mice.purchase.byCarrier')}</span>
          {s.byCarrier.map((c) => (
            <span key={c.carrier} className="text-meta text-foreground">
              {t('mice.purchase.carrierLine', {
                carrier: carrierName(c.carrier),
                count: c.tickets,
                amount: s.total
                  ? formatMoney({ amount: c.amount, currency: s.total.currency })
                  : '—',
              })}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Revisão antes de comprar: bilhetes, total, companhias e quem pede uma decisão. */
export function PurchaseReviewDialog(props: Readonly<PurchaseReviewDialogProps>) {
  const needs = props.needs ?? [];
  const [included, setIncluded] = useState<ReadonlySet<string>>(new Set());
  const [choices, setChoices] = useState<ReadonlyMap<string, VoucherChoice>>(new Map());
  // Um clique só por abertura: o ref barra o segundo clique antes do re-render (o botão segue
  // na tela durante a animação de saída); o estado desabilita o botão.
  const submittedRef = useRef(false);
  const [submitted, setSubmitted] = useState(false);
  // Uma abertura nova (outra lista) zera a revisão no próprio render; a trava do clique
  // só é lida no clique, então zera depois do commit.
  const [shownNeeds, setShownNeeds] = useState(props.needs);
  if (shownNeeds !== props.needs) {
    setShownNeeds(props.needs);
    if (props.needs !== null) {
      setSubmitted(false);
      setIncluded(new Set());
      setChoices(new Map());
    }
  }
  useEffect(() => {
    if (props.needs !== null) submittedRef.current = false;
  }, [props.needs]);

  const chosen = needs.filter(
    (n) => !checkPending(n) && (!pendingOf(n) || included.has(n.id) || choices.has(n.id)),
  );
  const skipped: MiceSkippedLeg[] = [...choices].flatMap(([needId, choice]) =>
    choice === 'buy' ? [] : [{ needId, direction: choice }],
  );
  const skip: MicePurchaseSkip = new Map(skipped.map((s) => [s.needId, new Set([s.direction])]));
  const after = summarizePurchase(chosen, skip, props.priceTolerance);
  const choose = (id: string, value: VoucherChoice | undefined): void => {
    const next = new Map(choices);
    if (value) next.set(id, value);
    else next.delete(id);
    setChoices(next);
  };
  const toggle = (id: string, on: boolean): void => {
    const next = new Set(included);
    if (on) next.add(id);
    else next.delete(id);
    setIncluded(next);
  };
  const voucherControl = (need: MiceNeedView): React.JSX.Element => (
    <VoucherChoices
      need={need}
      value={choices.get(need.id)}
      onChange={(value) => {
        choose(need.id, value);
      }}
    />
  );
  const incompleteControl = (need: MiceNeedView): React.JSX.Element => (
    <IncompleteControls
      need={need}
      included={included.has(need.id)}
      onToggle={(on) => {
        toggle(need.id, on);
      }}
      onCompleteProfile={props.onCompleteProfile}
    />
  );
  // Voucher com checagem pendente vai para o cadastro: a escolha do voucher compraria.
  const withVoucher = needs.filter((n) => voucherAlert(n) !== null && !checkPending(n));
  const incomplete = needs.filter(
    (n) => isIncomplete(n) && (voucherAlert(n) === null || checkPending(n)),
  );

  return (
    <Dialog
      open={props.needs !== null}
      onOpenChange={(open) => {
        if (!open) props.onCancel();
      }}
    >
      {/* Muitos avisos: só o miolo rola; título e botões ficam sempre na tela. */}
      <DialogContent className="overflow-hidden">
        <DialogTitle>{t('mice.purchase.title', { count: needs.length })}</DialogTitle>
        <p className="text-meta text-muted-foreground">{t('mice.purchase.sub')}</p>
        <div className="-mx-6 min-h-0 flex-1 space-y-4 overflow-y-auto px-6">
          <Totals needs={needs} />
          {withVoucher.length + incomplete.length > 0 ? (
            <span className="block text-micro font-medium text-muted-foreground">
              {t('mice.purchase.resolve')}
            </span>
          ) : null}
          <PendingGroup
            title={t('mice.needs.order.voucherWarning', { count: withVoucher.length })}
            tone="amber"
            needs={withVoucher}
            detail={voucherAlert}
            control={voucherControl}
          />
          <PendingGroup
            title={t('mice.needs.order.incompleteWarning', { count: incomplete.length })}
            tone="red"
            needs={incomplete}
            detail={() => null}
            control={incompleteControl}
          />
          <div className="rounded-md bg-muted p-3 text-meta text-foreground-default">
            <span className="font-medium text-foreground">{t('mice.purchase.how')}</span>
            <p className="mt-1">{t('mice.purchase.howBody')}</p>
          </div>
          <p className="text-meta text-muted-foreground">
            {after.travelers === 0
              ? t('mice.purchase.none')
              : t('mice.purchase.after', {
                  travelers: t('mice.travelers.count', { count: after.travelers }),
                  tickets: t('mice.purchase.ticketCount', { count: after.tickets }),
                  total: after.total ? formatMoney(after.total) : '—',
                })}
          </p>
          {after.ceiling ? (
            <p className="text-meta text-muted-foreground">
              {t('mice.purchase.ceiling', {
                amount: formatMoney(after.ceiling),
                percent: new Intl.NumberFormat(currentLocale(), { style: 'percent' }).format(
                  props.priceTolerance,
                ),
              })}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 justify-end gap-2">
          <Button variant="outline" onClick={props.onCancel}>
            {t('mice.purchase.cancel')}
          </Button>
          <Button
            disabled={submitted || chosen.length === 0}
            onClick={() => {
              if (submittedRef.current) return;
              submittedRef.current = true;
              setSubmitted(true);
              props.onConfirm(
                chosen.map((n) => n.id),
                skipped,
              );
            }}
          >
            {t('mice.purchase.confirm', { count: after.tickets })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
